import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Buildings,
  CheckCircle,
  Wrench,
  X,
  Tag,
  Cube,
  User,
  Envelope,
  Wall,
  Clock,
  GitMerge,
  ChatText,
  ArrowLeft,
} from '@phosphor-icons/react';
import { Avatar, Tooltip } from '@heroui/react';
import DOMPurify from 'dompurify';
import { PickerButton } from 'screens/helpdesk/components/pickerButton';
import { getUserImageUrlById, getUserName } from 'shared/utils/users';
import type { CellConfig } from 'shared/components/UniversalTable/components';
import { TABLE_DESIGN } from 'shared/components/UniversalTable/constants/tableDesign';
import { RenoTicketStatus, getRenoCommunicationCode } from '../types/renoTicket';
import {
  TenantBalanceBadge,
  tenantBalanceKey,
  type TenantBalance,
} from 'shared/components/tenant';
import { resolveRenoEmailChannel, RenoChannelEmail } from '../utils/renoEmailChannel';
import { getRenoCategoryStyle } from '../utils/renoCategoryColor';

const renderHTML = (html: string) => ({ __html: DOMPurify.sanitize(html) });
const isHTML = (str: string) => /<[a-z][\s\S]*>/i.test(str);

/** Compact bedrag voor de smalle tabelkolommen (geen decimalen, wel het euroteken). */
const formatRenoAmount = (value: number) =>
  new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);

// Fase-pill: zelfde styling als de helpdesk-fasekolom (afgeronde pill met bg/tekst-kleur).
const STATUS_PILL: Record<RenoTicketStatus, { bg: string; text: string }> = {
  [RenoTicketStatus.UNKNOWN]: { bg: 'bg-gray-50', text: 'text-gray-700' },
  [RenoTicketStatus.GEEN_REACTIE]: { bg: 'bg-red-50', text: 'text-red-700' },
  [RenoTicketStatus.LOPENDE]: { bg: 'bg-blue-50', text: 'text-blue-700' },
  [RenoTicketStatus.INFO_ONTVANGEN]: { bg: 'bg-teal-50', text: 'text-teal-700' },
  [RenoTicketStatus.AFGEROND]: { bg: 'bg-green-50', text: 'text-green-700' },
};

/**
 * Aantal mails per kanaal voor de RS-/RT-knoppen in de lijst.
 *
 * Bron is de backend (`GET /reno-helpdesk/tickets` en `.../grouped` leveren
 * `supplierEmailCount` / `tenantEmailCount` uit één aggregatiequery). Enkel wanneer
 * die velden ontbreken, wordt er op de meegeladen mails geteld — met dezelfde
 * kanaalregel als elders, zodat de tellers niet uit elkaar lopen.
 *
 * Een mail **zonder kanaal** telt bij geen van beide knoppen. Zo'n mail is doorgaans
 * de mail die het ticket aangemaakt heeft: er is dan nog niets via het ticket
 * gecommuniceerd, en toch stond er meteen een cijfer op de RS-knop. Een teller loopt
 * pas op wanneer er effectief gemaild is — verzenden zet `metadata.channel` en een
 * antwoord draagt de RS-/RT-code in het onderwerp. De mails zelf blijven in beide
 * kanaalweergaven staan; `unknownCount` voedt de tooltip.
 */
const countRenoEmails = (
  ticket: {
    supplierEmailCount?: number;
    tenantEmailCount?: number;
    unknownEmailCount?: number;
    supplierUnreadEmailCount?: number;
    tenantUnreadEmailCount?: number;
  },
  emails: RenoChannelEmail[],
) => {
  if (typeof ticket?.supplierEmailCount === 'number' || typeof ticket?.tenantEmailCount === 'number') {
    return {
      supplierCount: ticket.supplierEmailCount ?? 0,
      tenantCount: ticket.tenantEmailCount ?? 0,
      unknownCount: ticket.unknownEmailCount ?? 0,
      supplierUnread: ticket.supplierUnreadEmailCount ?? 0,
      tenantUnread: ticket.tenantUnreadEmailCount ?? 0,
    };
  }

  let supplierCount = 0;
  let tenantCount = 0;
  let unknownCount = 0;
  let supplierUnread = 0;
  let tenantUnread = 0;
  for (const email of emails) {
    const resolved = resolveRenoEmailChannel(email);
    const ongelezen = (email as any).direction === 'received' && !(email as any).isRead;
    // Niet toewijsbaar → wél in beide kanaalweergaven zichtbaar, maar bij geen van
    // beide tellers: zie de toelichting hierboven.
    if (resolved === 'supplier') {
      supplierCount += 1;
      if (ongelezen) supplierUnread += 1;
    } else if (resolved === 'tenant') {
      tenantCount += 1;
      if (ongelezen) tenantUnread += 1;
    } else {
      unknownCount += 1;
    }
  }
  return { supplierCount, tenantCount, unknownCount, supplierUnread, tenantUnread };
};

export interface RenoCellFactoryProps {
  ticket: any;
  context: {
    isSelected: boolean;
    isBulkActive: boolean;
    onToggleSelect: (selected: boolean) => void;
    onClearSelection: () => void;
  };
  handlers: {
    onComplete: (e: React.MouseEvent) => void;
    /** Opent de redenmodal om een afgerond ticket te heropenen. */
    onReopen: () => void;
    onMerge: () => void;
    onDelete: () => void;
    onTitleClick: (e: React.MouseEvent) => void;
  };
  renderPriority: (priority: any, compact: boolean) => React.ReactNode;
  users: any[];
  /**
   * Openstand per huurder, gesleuteld op `contact:<id>` — één call voor de hele pagina
   * (zie `useTenantBalances`). Ontbreekt een sleutel, dan is er geen openstand en toont
   * het badge niets.
   */
  tenantBalances?: Record<string, TenantBalance>;
  /**
   * Extra kolomvarianten voor de per-project weergave van de lijst.
   *
   * Met `withProjectColumns` krijgen de rijen een eigen "Project"- en
   * "Onderdeel"-kolom (beide met picker) en verdwijnen de project-/onderdeel-
   * badges uit de titelkolom, zodat die info niet dubbel staat.
   * Zie {@link file://./../configs/renoTableConfig.tsx} `RENO_PROJECT_COLUMNS_CONFIG`.
   */
  options?: { withProjectColumns?: boolean };
}

const dispatchPicker = (mode: string, ticketId: number) => {
  window.dispatchEvent(new CustomEvent('helpdesk-open-picker', { detail: { mode, ticketId } }));
};

/**
 * Cell-configuratie voor renovatietickets — visueel identiek aan de helpdesk-rij.
 */
export const createRenoCells = ({
  ticket,
  context,
  handlers,
  renderPriority,
  users,
  options,
  tenantBalances,
}: RenoCellFactoryProps): CellConfig[] => {
  // E-mailtellers per kanaal komen uit de backend (`supplierEmailCount` /
  // `tenantEmailCount`): de lijstquery laadt de mails zelf niet mee, dus zelf tellen
  // op `ticket.emails` gaf altijd 0. De terugval blijft staan voor de plekken die de
  // mails wél meesturen (bv. het ticketdetail).
  const emails = (ticket.emails ?? []) as RenoChannelEmail[];
  const { supplierCount, tenantCount, unknownCount, supplierUnread, tenantUnread } =
    countRenoEmails(ticket, emails);
  // Uitleg bij mails die aan geen kanaal toegewezen konden worden: die tellen niet mee,
  // maar zijn wel in beide kanaalweergaven zichtbaar (o.a. de mail die het ticket
  // aanmaakte). Zonder deze zin lijkt het verloop meer mails te tonen dan de knop zegt.
  const zonderKanaal =
    unknownCount > 0
      ? ` — ${unknownCount} mail(s) zonder kanaal, niet meegeteld, wel zichtbaar in beide weergaven`
      : '';
  const onderdeelLabel = ticket.projectPart?.label?.name || ticket.projectPart?.title || null;
  const tags = (ticket.tags ?? []) as string[];
  // Bedragen uit de gekoppelde financiële documenten (excl. btw, creditnota's afgetrokken).
  const quoteAmount = Number(ticket.approvedQuoteAmount ?? 0) || 0;
  const invoiceAmount = Number(ticket.invoiceAmount ?? 0) || 0;
  const invoiceNumbers = (ticket.invoiceNumbers ?? []) as string[];
  const pill = STATUS_PILL[ticket.status as RenoTicketStatus] ?? STATUS_PILL[RenoTicketStatus.UNKNOWN];
  const withProjectColumns = options?.withProjectColumns ?? false;

  // Project- en onderdeelkolom: enkel in de per-project weergave. Beide zijn
  // pickers, zodat een ticket vanuit die weergave naar een ander project of
  // onderdeel verplaatst kan worden.
  const projectColumns: CellConfig[] = withProjectColumns
    ? [
        {
          id: 'project',
          colSpan: 1,
          align: 'left',
          content: (
            <PickerButton
              mode="project"
              ticketId={ticket.id}
              value={ticket.renovationProject?.title || null}
              placeholder="+ Project"
              icon={<Wall className="text-neutral-secondary-mid h-3 w-3" />}
              selectedTicketIds={[]}
              onClearSelection={context.onClearSelection}
            />
          ),
        },
        {
          id: 'project-part',
          colSpan: 1,
          align: 'left',
          content: (
            <PickerButton
              mode="part"
              ticketId={ticket.id}
              value={onderdeelLabel}
              placeholder="+ Onderdeel"
              icon={<Tag className="text-neutral-secondary-mid h-3 w-3" />}
              selectedTicketIds={[]}
              onClearSelection={context.onClearSelection}
            />
          ),
        },
      ]
    : [];

  return [
    // Checkbox
    {
      id: 'checkbox',
      colSpan: 1,
      align: 'center',
      content: (
        <Checkbox
          checked={context.isSelected}
          onCheckedChange={(checked) => context.onToggleSelect(checked === true)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    // Acties — afronden, samenvoegen, verwijderen (zoals helpdesk)
    {
      id: 'actions',
      colSpan: 1,
      align: 'center',
      content: (
        <div className="flex items-center justify-center gap-1">
          {ticket.status === RenoTicketStatus.AFGEROND ? (
            /* Gele terug-naar-open pijl bij een afgerond ticket, zoals in de helpdesk.
               Heropenen vraagt hier wel een reden (die wordt als actienota bewaard),
               dus dit opent de redenmodal i.p.v. meteen te wijzigen. */
            <button
              onClick={(e) => { e.stopPropagation(); handlers.onReopen(); }}
              className="shrink-0 rounded-md bg-yellow-100 p-1 text-xs text-yellow-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-yellow-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-yellow-300 active:scale-95"
              title="Ticket heropenen"
            >
              <ArrowLeft className="h-3 w-3" />
            </button>
          ) : (
            <button
              onClick={handlers.onComplete}
              className="shrink-0 rounded-md bg-green-100 p-1 text-xs text-green-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-green-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-green-300 active:scale-95"
              title="Ticket afronden"
            >
              <CheckCircle className="h-3 w-3" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handlers.onMerge(); }}
            className="shrink-0 rounded-md bg-gray-100 p-1 text-xs text-gray-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-blue-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-blue-300 active:scale-95"
            title="Tickets samenvoegen"
          >
            <GitMerge className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handlers.onDelete(); }}
            className="shrink-0 rounded-md bg-gray-100 p-1 text-xs text-gray-600 shadow-sm transition-all duration-200 ease-in-out hover:scale-110 hover:bg-red-600 hover:text-white hover:shadow-md focus:ring-2 focus:ring-red-300 active:scale-95"
            title="Ticket verwijderen"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ),
    },
    // Comm — leverancier (RS) en huurder (RT). Rode markering met pulserende stip
    // zodra er een ontvangen mail nog niet gelezen is, net als in de helpdesk.
    {
      id: 'comm',
      colSpan: 1,
      align: 'center',
      content: (
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); window.location.href = `/renotickets/${ticket.id}?channel=supplier`; }}
            className={`relative flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium shadow-sm transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-md focus:ring-2 active:scale-95 ${
              supplierUnread > 0
                ? 'bg-red-100 text-red-600 ring-1 ring-red-200 hover:bg-red-600 hover:text-white focus:ring-red-300'
                : 'bg-neutral-primary/10 text-neutral-primary hover:bg-neutral-primary hover:text-white focus:ring-neutral-primary/20'
            }`}
            title={`Communicatie leverancier (RS) — ${supplierCount} mail(s)${
              supplierUnread > 0 ? `, ${supplierUnread} ongelezen` : ''
            }${zonderKanaal}`}
          >
            <Wrench className="h-3 w-3" />
            <span className="text-[11px] font-semibold">{supplierCount}</span>
            {supplierUnread > 0 && (
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
                </span>
              </div>
            )}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); window.location.href = `/renotickets/${ticket.id}?channel=tenant`; }}
            className={`relative flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium shadow-sm transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-md focus:ring-2 active:scale-95 ${
              tenantUnread > 0
                ? 'bg-red-100 text-red-600 ring-1 ring-red-200 hover:bg-red-600 hover:text-white focus:ring-red-300'
                : 'bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white focus:ring-blue-300'
            }`}
            title={`Communicatie huurder (RT) — ${tenantCount} mail(s)${
              tenantUnread > 0 ? `, ${tenantUnread} ongelezen` : ''
            }${zonderKanaal}`}
          >
            <Envelope className="h-3 w-3" />
            <span className="text-[11px] font-semibold">{tenantCount}</span>
            {tenantUnread > 0 && (
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
                </span>
              </div>
            )}
          </button>
        </div>
      ),
    },
    // Prio
    {
      id: 'priority',
      colSpan: 1,
      align: 'center',
      content: renderPriority(ticket.priority, true),
    },
    // Project + Onderdeel (enkel per-project weergave)
    ...projectColumns,
    // Ticket & Label — renocode (RS-id) + titel + onderdeel-label badge + projectcode badge
    {
      id: 'ticket-label',
      colSpan: 1,
      align: 'left',
      content: (
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            <span className={TABLE_DESIGN.cellContent.text.id}>{getRenoCommunicationCode(ticket.id)}</span>
            <Tooltip
              content={
                <div className="max-h-64 max-w-md overflow-y-auto p-3">
                  <div className="text-neutral-primary mb-2 border-b border-gray-200 pb-2 text-base font-semibold break-words whitespace-pre-wrap">
                    {ticket.title}
                  </div>
                  <div className="text-neutral-primary mb-1 text-xs font-medium tracking-wide uppercase">Beschrijving</div>
                  <div className="text-sm text-gray-600">
                    {ticket.description ? (
                      isHTML(ticket.description) ? (
                        <div className="break-words whitespace-pre-wrap" dangerouslySetInnerHTML={renderHTML(ticket.description)} />
                      ) : (
                        <div className="break-words whitespace-pre-wrap">
                          {ticket.description.length > 300 ? `${ticket.description.substring(0, 300)}...` : ticket.description}
                        </div>
                      )
                    ) : (
                      'Geen beschrijving beschikbaar'
                    )}
                  </div>
                </div>
              }
              placement="top"
              delay={500}
            >
              <button
                onClick={handlers.onTitleClick}
                className={`${TABLE_DESIGN.cellContent.text.title} ${ticket.status === RenoTicketStatus.GEEN_REACTIE ? TABLE_DESIGN.cellContent.text.titleUnread : ''} min-w-0 flex-1 truncate text-left`}
                title="Klik om ticket te openen"
              >
                {ticket.title}
              </button>
            </Tooltip>
            {/* Onderdeel-label badge (opent onderdeel-picker) — niet in de
                per-project weergave, daar staat het in een eigen kolom. */}
            <div className={`shrink-0 ${withProjectColumns ? 'hidden' : ''}`}>
              <button
                onClick={(e) => { e.stopPropagation(); dispatchPicker('part', ticket.id); }}
                className={`${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.interactive} ${TABLE_DESIGN.cellContent.badge.compact} whitespace-nowrap ${
                  onderdeelLabel ? 'bg-moss-primary/10 text-moss-primary' : 'bg-brick-primary text-white'
                }`}
              >
                {onderdeelLabel ? (
                  <>
                    <Tag className="h-3 w-3" />
                    <span className="whitespace-nowrap">{onderdeelLabel}</span>
                  </>
                ) : (
                  <Tag className="h-4 w-4" />
                )}
              </button>
            </div>
            {/* Renovatieproject badge — idem: enkel in de vlakke weergave. */}
            {!withProjectColumns && ticket.renovationProject && (
              <div className="shrink-0">
                <Tooltip content={`Renovatieproject: ${ticket.renovationProject.title}`} placement="top" delay={300}>
                  <Link
                    to={`/renovation/${ticket.renovationProject.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`${TABLE_DESIGN.cellContent.badge.base} ${TABLE_DESIGN.cellContent.badge.compact} inline-flex items-center gap-1 whitespace-nowrap bg-amber-100 text-amber-800 hover:bg-amber-200`}
                  >
                    <Wall className="h-3 w-3 shrink-0" />
                    <span className="max-w-[100px] truncate text-xs">RENO-{ticket.renovationProject.id}</span>
                  </Link>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ),
    },
    // Gebouw
    {
      id: 'building',
      colSpan: 1,
      align: 'center',
      hidden: true,
      content: (
        <PickerButton
          mode="building"
          ticketId={ticket.id}
          value={ticket.buildingId || ticket.building?.id || null}
          icon={<Buildings className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Unit
    {
      id: 'unit',
      colSpan: 1,
      align: 'center',
      hidden: true,
      content: (
        <PickerButton
          mode="unit"
          ticketId={ticket.id}
          value={ticket.unitId || ticket.unit?.id || null}
          icon={<Cube className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Type boodschap — via de picker-modal, net als de andere kolommen. Een
    // dropdown in de cel zelf was onzichtbaar: tabelcellen staan op
    // `overflow-hidden` (zie tableStyles.getCellContainerClass).
    {
      id: 'message-type',
      colSpan: 1,
      align: 'center',
      className: 'hidden xl:flex',
      content: (
        <PickerButton
          mode="messageType"
          ticketId={ticket.id}
          value={ticket.messageType ?? null}
          placeholder="Type..."
          icon={<ChatText className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Categorieën — stonden enkel op de detailpagina, niet in de lijst. Klikken opent
    // dezelfde categoriecatalogus als daar (meerdere categorieën mogelijk).
    //
    // Elke categorie krijgt haar eigen chip met de kleur van de helpdesk
    // (`getRenoCategoryStyle`): "Verwarming" is hier dus even oranje als op een
    // helpdeskticket. Voordien was alles één vlak moss-tintje en zag je in de lijst
    // niet welke categorie het was zonder te lezen.
    {
      id: 'tags',
      colSpan: 1,
      align: 'left',
      hidden: true,
      content: (
        <PickerButton
          mode="tags"
          ticketId={ticket.id}
          value={tags.length ? tags.join(', ') : null}
          display={
            <span className="flex min-w-0 items-center gap-1" title={tags.join(', ')}>
              {tags.map((categorie) => {
                const { icon, bgColor, textColor } = getRenoCategoryStyle(categorie);
                return (
                  <span
                    key={categorie}
                    className={`inline-flex min-w-0 shrink items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium ${bgColor} ${textColor}`}
                  >
                    <span className="shrink-0">{icon}</span>
                    <span className="truncate">{categorie}</span>
                  </span>
                );
              })}
            </span>
          }
          placeholder="+ Categorie"
          icon={tags.length ? undefined : <Tag className="text-neutral-secondary-mid h-3 w-3" />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Contactpersoon (was Melder), met de openstand van die huurder ernaast.
    {
      id: 'reporter',
      colSpan: 1,
      align: 'center',
      hidden: true,
      content: (
        <div className="flex min-w-0 items-center gap-1">
          <span className="min-w-0 flex-1">
            <PickerButton
              mode="reporter"
              ticketId={ticket.id}
              value={ticket.reporter?.firstName ? `${ticket.reporter.firstName} ${ticket.reporter.lastName}` : null}
              icon={<User className="text-neutral-secondary-mid h-3 w-3" />}
              selectedTicketIds={[]}
              onClearSelection={context.onClearSelection}
            />
          </span>
          {/* Openstand naast de naam. Het badge staat buiten de knop: het is zelf een
              link naar het incassodossier, en een link in een knop is ongeldige HTML. */}
          <TenantBalanceBadge
            name={
              ticket.reporter?.firstName
                ? `${ticket.reporter.firstName} ${ticket.reporter.lastName}`
                : undefined
            }
            balance={tenantBalances?.[tenantBalanceKey('contact', ticket.reporter?.id) ?? '']}
          />
        </div>
      ),
    },
    // Leverancier(s) — meerdere mogelijk; klikken voegt een leverancier toe.
    {
      id: 'supplier',
      colSpan: 1,
      align: 'center',
      hidden: true,
      content: (() => {
        const assignments = (ticket.supplierAssignments ?? []) as any[];
        const names = assignments.map((a) => a.supplier?.name).filter(Boolean);
        const display = names.length ? names.join(', ') : ticket.supplier?.name || null;
        return (
          <PickerButton
            mode="supplier"
            ticketId={ticket.id}
            value={display}
            // Meerdere leveranciers passen niet in de kolombreedte en werden afgekapt,
            // dus las je er één en leek dat de enige — terwijl het e-mailverloop dan
            // over een andere sprak. Het aantal staat er bij en de tooltip toont ze
            // allemaal, zodat een verkeerd gekoppelde leverancier opvalt.
            display={
              <span className="flex min-w-0 items-center gap-1" title={names.join('\n')}>
                <span className="truncate">{display}</span>
                {names.length > 1 && (
                  <span className="shrink-0 rounded-full bg-neutral-primary/10 px-1 text-[10px] font-semibold text-neutral-primary">
                    {names.length}
                  </span>
                )}
              </span>
            }
            placeholder="+ Leverancier"
            icon={<Wrench className="text-neutral-secondary-mid h-3 w-3" />}
            selectedTicketIds={[]}
            onClearSelection={context.onClearSelection}
          />
        );
      })(),
    },
    // Toegewezen (interne verantwoordelijke)
    {
      id: 'assignee',
      colSpan: 1,
      align: 'center',
      content: (
        <PickerButton
          mode="assignee"
          ticketId={ticket.id}
          value={ticket.assignedTo ? getUserName(ticket.assignedTo, users).name : null}
          icon={<Avatar className="h-4 w-4" src={getUserImageUrlById(ticket.assignedTo, users)} />}
          selectedTicketIds={[]}
          onClearSelection={context.onClearSelection}
        />
      ),
    },
    // Fase — klikbaar; opent de fasekiezer (was een niet-interactieve pill,
    // waardoor de fase enkel via het ticketdetail of een bulkactie te wijzigen was).
    {
      id: 'internal-status',
      colSpan: 1,
      align: 'left',
      content: (
        <button
          onClick={(e) => { e.stopPropagation(); dispatchPicker('phase', ticket.id); }}
          title={`${ticket.status} — klik om de fase te wijzigen`}
          // De kolom is smal (plaats gemaakt voor "Ticket & Label"), dus kapt de pill af
          // met "…" i.p.v. hard weggeknipt te worden door de `overflow-hidden` van de cel.
          // De volledige fase staat in de tooltip.
          className={`flex min-w-0 max-w-full items-center rounded-md px-2 py-1 ${pill.bg} whitespace-nowrap transition-all duration-200 ease-in-out hover:shadow-sm focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden`}
        >
          <span className={`truncate text-xs font-medium ${pill.text}`}>{ticket.status}</span>
        </button>
      ),
    },
    // Interventiedatum — klikbaar; opent de datumkiezer.
    {
      id: 'intervention-date',
      colSpan: 1,
      align: 'center',
      hidden: true,
      content: (
        <button
          onClick={(e) => { e.stopPropagation(); dispatchPicker('interventionDate', ticket.id); }}
          title={ticket.interventionDate ? 'Interventiedatum wijzigen' : 'Interventiedatum instellen'}
          className={`w-full truncate rounded-sm border border-neutral-primary/20 px-1.5 py-1 text-left text-[11px] transition-all duration-200 hover:border-moss-primary/40 hover:bg-neutral-primary/5 focus:ring-2 focus:ring-moss-primary/20 focus:outline-hidden ${
            ticket.interventionDate ? 'text-neutral-primary' : 'text-neutral-secondary-mid'
          }`}
        >
          {ticket.interventionDate
            ? new Date(ticket.interventionDate).toLocaleDateString('nl-BE', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
              })
            : '+ Datum'}
        </button>
      ),
    },
    // Goedgekeurd offertebedrag — som van de gekoppelde goedgekeurde offertes.
    {
      id: 'quote-amount',
      colSpan: 1,
      align: 'right',
      className: 'hidden xl:flex',
      content: (
        <span
          className={`text-[11px] font-medium ${quoteAmount ? 'text-neutral-primary' : 'text-neutral-secondary-mid'}`}
          title="Som van de gekoppelde goedgekeurde offertes (excl. btw)"
        >
          {quoteAmount ? formatRenoAmount(quoteAmount) : '—'}
        </span>
      ),
    },
    // Factuurbedrag — som van de gekoppelde facturen, creditnota's afgetrokken.
    {
      id: 'invoice-amount',
      colSpan: 1,
      align: 'right',
      className: 'hidden xl:flex',
      content: (
        <span
          className={`text-[11px] font-semibold ${
            invoiceAmount < 0
              ? 'text-red-600'
              : invoiceAmount
                ? 'text-neutral-primary'
                : 'text-neutral-secondary-mid'
          }`}
          title={
            invoiceNumbers.length
              ? `Facturen: ${invoiceNumbers.join(', ')} (excl. btw, creditnota's afgetrokken)`
              : 'Nog geen factuur gekoppeld'
          }
        >
          {invoiceAmount ? formatRenoAmount(invoiceAmount) : '—'}
        </span>
      ),
    },
    // Dagen in Fase
    {
      id: 'days-in-phase',
      colSpan: 1,
      align: 'center',
      content: (() => {
        const days = Math.max(0, Math.floor((Date.now() - new Date(ticket.updatedAt).getTime()) / 86400000));
        return (
          <div className="flex items-center justify-center gap-1">
            <Clock
              weight="duotone"
              size={14}
              className={days > 7 ? 'text-red-500' : days > 3 ? 'text-orange-500' : 'text-gray-500'}
            />
            <span className={`text-xs font-medium ${days > 7 ? 'text-red-700' : days > 3 ? 'text-orange-700' : 'text-gray-600'}`}>
              {days}d
            </span>
          </div>
        );
      })(),
    },
    // Datum
    {
      id: 'date',
      colSpan: 1,
      align: 'center',
      hidden: true,
      content: <div className="text-neutral-secondary-mid text-[11px]">{new Date(ticket.createdAt).toLocaleDateString('nl-NL')}</div>,
    },
  ];
};
